import * as Sentry from "@sentry/react";

/**
 * Frontend error monitoring (Sentry).
 *
 * Symmetric to the backend setup: initialise before React mounts so that
 * even errors thrown during the very first render are captured. Errors are
 * filtered to suppress predictable noise (401s after token expiry,
 * ResizeObserver loop limits, network blips) so the inbox stays signal-rich.
 */
const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const APP_ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV || "development";

const NOISE_PATTERNS = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  "Non-Error promise rejection captured",
  "Network Error",
  "Failed to fetch",
  "Load failed",
  "AbortError: The user aborted a request",
];

function isNoise(event, hint) {
  const exc = hint?.originalException;

  // 401 after token expiry — not a real error
  if (exc && (exc.status === 401 || exc.response?.status === 401)) return true;

  // Aborted fetch on route change
  if (exc && exc.name === "AbortError") return true;

  const msg = (event?.message || exc?.message || "").toString();
  return NOISE_PATTERNS.some((p) => msg.includes(p));
}

export function initSentry() {
  if (!DSN) {
    // Quiet in dev / when DSN unset. No throw — frontend must always boot.
    // eslint-disable-next-line no-console
    console.info("[sentry] disabled (REACT_APP_SENTRY_DSN not set)");
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: APP_ENV,
    // 100% sample rate during dev phase (matches backend). Drop to ~0.1 in prod
    // once we have enough signal.
    tracesSampleRate: 1.0,
    // Replays are sampled lower because they're heavier than plain traces.
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    // Don't attach IP / cookie data by default — we set user context
    // explicitly from AuthContext via setSentryUser().
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend(event, hint) {
      if (isNoise(event, hint)) return null;
      return event;
    },
  });
  // eslint-disable-next-line no-console
  console.info(`[sentry] initialised (env=${APP_ENV})`);
}

/**
 * Attach (or clear) the authenticated user context for subsequent error
 * events. Called from AuthContext whenever the `user` state changes.
 */
export function setSentryUser(user) {
  if (!DSN) return;
  if (user && user !== false) {
    Sentry.setUser({
      id: user.id || user._id,
      email: user.email,
      // `username` is the field Sentry's feedback widget reads for the
      // "Name" pre-fill (configured via useSentryUser.name = "username").
      username: user.name || (user.email ? user.email.split("@")[0] : undefined),
      role: user.role,
    });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
